import type { ReactElement } from 'react';
import { notFound, redirect } from 'next/navigation';
import { VpnError } from '../components/VpnError';
import {
  isInvalidInputError,
  isNotAuthenticatedError,
  isNotFoundError,
  isVPNConnectionError,
} from './errorHelpers';

/**
 * A utility that consistently handles server component errors.
 * @param error - The error that was thrown
 * @param customPreemptiveHandler @optional - Called before global error handling, giving the server component to respond to specific error cases.
 *   - (when it returns) ReactElement | null: The error was "handled" and global error handling will not be attempted
 *   - (when it returns) undefined: The error was not handled and global error handling will be attempted
 * @throws Re-throws the error if it was not handled by either the preemptive handler or the global error handling checks
 */
export const handleGlobalErrors = (
  error: unknown,
  preemptiveCustomHandler?: (e: unknown) => ReactElement<any> | null | undefined
): ReactElement<any> | null => {
  // Run the preemptive handler first
  if (preemptiveCustomHandler) {
    const result = preemptiveCustomHandler(error);
    if (result !== undefined) {
      return result;
    }
  }

  if (isInvalidInputError(error)) {
    notFound();
  }

  if (isNotFoundError(error)) {
    notFound();
  }

  /**
   * Last resort fallback - this error should have been handled by the middleware
   */
  if (isNotAuthenticatedError(error)) {
    // Redirect to a route handler so we can track instances of this error
    redirect('/auth/login');
  }

  if (isVPNConnectionError(error)) {
    return <VpnError reloadOnceConnected={true} />;
  }

  // The error was not handled, rethrow it
  throw error;
};
